import React, { useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import PreviewCanvas from './PreviewCanvas';
import AiBanner from './AiBanner';
import { ApplyVideoWorkspaceBlocks } from '../../../Client/ManagementServiceClient';
import './effectBlocks.css';

const DEFAULT_PARAMS = { x: 0.1, y: 0.1, w: 0.8, h: 0.8 };

/**
 * CropBlockEditor — phase-2 effect block: drag a "keep" rectangle to reframe a
 * clip, then compile it into the plan via ApplyBlocks -> crop_tool. Reuses the
 * box's move + corner-resize interaction on PreviewCanvas.
 *
 * Props: videoWorkspaceId, frameUrl, sourceFileName, mediaInfo, onApplied, onClose
 */
function CropBlockEditor({ videoWorkspaceId, frameUrl, sourceFileName, mediaInfo, aiSuggestion, onApplied, onClose }) {
    const [params, setParams] = useState(() => ({ ...DEFAULT_PARAMS, ...(aiSuggestion?.params || {}) }));
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState('');
    const [measured, setMeasured] = useState(() => (
        Number(mediaInfo?.width) > 0 && Number(mediaInfo?.height) > 0 ? mediaInfo : null
    ));

    const aspectRatio = useMemo(() => {
        const w = Number(measured?.width) || 9;
        const h = Number(measured?.height) || 16;
        return w / h;
    }, [measured]);

    const block = useMemo(() => ({ id: 'crop-1', type: 'crop', source: sourceFileName, params }), [params, sourceFileName]);

    const outputDims = useMemo(() => {
        if (!measured?.width || !measured?.height) return null;
        return {
            w: Math.round((params.w || 0) * measured.width),
            h: Math.round((params.h || 0) * measured.height),
        };
    }, [params, measured]);

    const canApply = Boolean(
        videoWorkspaceId && sourceFileName &&
        Number(measured?.width) > 0 && Number(measured?.height) > 0 &&
        (params.w || 0) > 0.02 && (params.h || 0) > 0.02
    );

    const handleApply = async () => {
        if (!canApply) {
            setError('Draw a crop region and make sure the clip has loaded.');
            return;
        }
        setApplying(true);
        setError('');
        try {
            const idToken = await getAuth().currentUser.getIdToken();
            const outBlock = {
                id: 'crop-1',
                type: 'crop',
                source: sourceFileName,
                startTime: 0,
                endTime: null,
                params,
            };
            const media = {
                [sourceFileName]: {
                    width: Math.round(Number(measured.width)),
                    height: Math.round(Number(measured.height)),
                    duration: Number(measured.duration) || 0,
                },
            };
            const res = await ApplyVideoWorkspaceBlocks(idToken, videoWorkspaceId, [outBlock], media);
            if (res.status === 200) {
                onApplied?.(res.myJson);
            } else {
                setError(res.myJson?.detail || 'Failed to apply the crop.');
            }
        } catch (err) {
            setError('Error applying edit: ' + err.message);
        } finally {
            setApplying(false);
        }
    };

    return (
        <div className="eb-editor">
            <AiBanner rationale={aiSuggestion?.rationale} />
            <div className="eb-editor-grid">
                <PreviewCanvas
                    frameUrl={frameUrl}
                    isVideo
                    aspectRatio={aspectRatio}
                    blocks={[block]}
                    selectedId="crop-1"
                    onChange={(_id, next) => setParams(next)}
                    onSelect={() => {}}
                    onMeta={setMeasured}
                />
                <div className="eb-controls">
                    <h5>Crop</h5>
                    <p className="eb-hint">Drag the frame to move · drag a corner to resize. Everything outside is removed.</p>

                    <div className="eb-ctl">
                        <span>Output size <em>{outputDims ? `${outputDims.w}×${outputDims.h}` : '—'}</em></span>
                    </div>

                    <div className="eb-actions">
                        <button type="button" className="eb-btn" onClick={() => setParams(DEFAULT_PARAMS)}>Reset</button>
                    </div>

                    {error && <p className="eb-error">{error}</p>}

                    <div className="eb-actions">
                        {onClose && <button type="button" className="eb-btn" onClick={onClose}>Cancel</button>}
                        <button type="button" className="eb-btn primary" disabled={!canApply || applying} onClick={handleApply}>
                            {applying ? 'Applying…' : 'Add to timeline'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CropBlockEditor;
